<template>
  <div>
    <Card>
      <template #header>
        <div class="multi-panel-step-header">
          <!-- Left side badges and title -->
          <div class="header-left">
            <Badge icon="pi pi-clock">
              <template v-if="editingStep === 0">
                {{ editedTime }}
                <InputText
                  v-model="editedTime"
                  placeholder="Enter time"
                />
              </template>
              <template v-else>
                {{ steps[0]?.time || '0min' }}
              </template>
            </Badge>

            <template v-if="editingStep === 0">
              <Dropdown
                v-model="selectedOption"
                :options="selectValue"
                option-label="label"
              />
            </template>
            <template v-else>
              <Badge
                icon="pi pi-gauge"
                :severity="difficultySeverity(steps[0]?.difficulty || 'easy')"
                class="ml-2"
              >
                {{ steps[0]?.difficulty || 'Easy' }}
              </Badge>
            </template>

            <!-- Title section -->
            <div class="guide-title ml-4">
              <template v-if="editingStep === 0">
                <InputText
                  v-model="editedTitle"
                  placeholder="Enter guide title"
                  class="w-full"
                />
              </template>
              <template v-else>
                <h2 class="m-0 text-xl">
                  {{ steps[0]?.title || 'New Maintenance Guide' }}
                </h2>
              </template>
            </div>
          </div>

          <!-- Edit button on the far right -->
          <div class="header-right">
            <Button
              v-if="editingStep === 0"
              icon="pi pi-check"
              severity="secondary"
              @click="toggleHeaderSave(0)"
            />
            <Button
              v-else
              icon="pi pi-pencil"
              severity="secondary"
              @click="toggleHeaderEdit(0)"
            />
          </div>
        </div>
      </template>

      <template #content>
        <div class="multi-panel-step-body">
          <!-- Tools Panel (50% width) -->
          <Panel
            header="Tools"
            class="half-width-panel"
          >
            <ul>
              <!-- EDIT MODE: editedTools -->
              <template v-if="editingStep === 0">
                <li
                  v-for="(tool, i) in editedTools"
                  :key="i"
                  class="inline-li"
                >
                  <span class="pi pi-wrench p-1" />
                  <InputText
                    v-model="editedTools[i]"
                    placeholder="Add a tool"
                  />
                  <Button
                    icon="pi pi-times"
                    severity="danger"
                    rounded
                    variant="outlined"
                    aria-label="Cancel"
                    size="small"
                    class="ml-2"
                    @click="removeTool(i)"
                  />
                </li>
              </template>
              <!-- VIEW MODE: steps[0].tools -->
              <template v-else>
                <li
                  v-for="(tool, i) in (steps[0]?.tools || [])"
                  :key="i"
                >
                  <span class="pi pi-wrench p-1" />
                  {{ tool }}
                </li>
                <li
                  v-if="!(steps[0]?.tools?.length)"
                  class="text-500"
                >
                  <span class="pi pi-wrench p-1" />
                  No tools required
                </li>
              </template>
            </ul>
            <!-- "Add Tool" button only in edit mode -->
            <Button
              v-if="editingStep === 0"
              icon="pi pi-plus"
              label="Add Tool"
              class="mt-2"
              @click="addTool"
            />
          </Panel>

          <!-- Vertical Divider -->
          <Divider layout="vertical" />

          <!-- Parts Panel (50% width) -->
          <Panel
            header="Parts"
            class="half-width-panel"
          >
            <ul class="list-disc">
              <!-- EDIT MODE: editedParts -->
              <template v-if="editingStep === 0">
                <li
                  v-for="(part, i) in editedParts"
                  :key="i"
                >
                  <InputNumber v-model="part.qty" />
                  <InputText v-model="part.part" />
                  <InputText v-model="part.partNumber" />
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    class="ml-2"
                    @click="removePart(i)"
                  />
                </li>
              </template>
              <!-- VIEW MODE: steps[0].parts -->
              <template v-else>
                <li
                  v-for="(part, i) in (steps[0]?.parts || [])"
                  :key="i"
                >
                  {{ part.qty }} x {{ part.part }}
                  <span class="font-light text-500">
                    ({{ part.partNumber }})
                  </span>
                </li>
                <li
                  v-if="!(steps[0]?.parts?.length)"
                  class="text-500"
                >
                  No parts required
                </li>
              </template>
            </ul>
            <!-- "Add Part" button only in edit mode -->
            <Button
              v-if="editingStep === 0"
              icon="pi pi-plus"
              label="Add Part"
              class="mt-2"
              @click="addPart"
            />
          </Panel>
        </div>
      </template>
    </Card>

    <!-- Show at least one empty step -->
    <Card class="step-card mt-4">
      <template #header>
        <div class="step-header">
          <span class="step-number">Step 1</span>
          <div class="step-title">
            {{ steps[1]?.title || "New Guide" }}
          </div>
          <div class="action-buttons">
            <Button
              icon="pi pi-pencil"
              severity="secondary"
              @click="toggleEdit(1)"
            />
          </div>
        </div>
      </template>

      <template #content>
        <div class="step-body">
          <div class="step-image">
            <template v-if="steps[1]?.image">
              <Image
                :src="resolveImagePath(steps[1].image)" 
                :alt="steps[1].title || 'Step image'"
                class="w-full h-48 object-cover"
              />
            </template>
            <template v-else>
              <div class="holding-image">
                <i class="pi pi-image text-4xl text-500" />
              </div>
            </template>
            <template v-if="editingStep === 1">
              <input
                type="file"
                class="image-input"
                @change="onFileChange($event, 1)"
              >
            </template>
          </div>
          <div class="step-text">
            <div v-html="steps[1]?.text || '<p>Click the edit button to add guide instructions.</p>'" />
          </div>
        </div>
      </template>
    </Card>

    <!-- Show additional steps if they exist -->
    <Card
      v-for="(step, index) in steps.slice(2)"
      :key="index + 2"
      class="step-card"
    >
      <template #header>
        <div class="step-header">
          <span class="step-number">Step {{ index + 1 }}</span>
          <div class="step-title">
            <template v-if="editingStep === index">
              <InputText
                v-model="editedStepTitle"
                placeholder="Edit title"
              />
            </template>
            <template v-else>
              {{ step.title || "Untitled Step" }}
            </template>
          </div>
          <div class="action-buttons">
            <Button
              v-if="editingStep === index"
              icon="pi pi-trash"
              severity="danger"
              class="delete-button"
              @click="confirmDelete(index)"
            />
            <Button
              v-if="editingStep === index"
              icon="pi pi-check"
              severity="secondary"
              @click="toggleSave(index)"
            />
            <Button
              v-else
              icon="pi pi-pencil"
              severity="secondary"
              @click="toggleEdit(index)"
            />
          </div>
        </div>
      </template>

      <template #content>
        <div class="step-body">
          <div class="step-image">
            <template v-if="step.image">
              <Image
                :src="resolveImagePath(step.image)" 
                :alt="step.title || 'Step image'"
                class="w-full h-48 object-cover"
              />
            </template>
            <template v-else>
              <div class="holding-image">
                <i class="pi pi-image text-4xl text-500" />
              </div>
            </template>
            <template v-if="editingStep === index">
              <input
                type="file"
                class="image-input"
                @change="onFileChange($event, index)"
              >
            </template>
          </div>
          <div class="step-text">
            <template v-if="editingStep === index">
              <Editor
                :key="editingStep"
                v-model="editedStepText"
                editor-style="height: 200px"
              />
            </template>
            <template v-else>
              <div v-html="step.text || '<p>No content yet.</p>'" />
            </template>
          </div>
        </div>
      </template>
    </Card>

    <!-- Button to add a step -->
    <Button
      icon="pi pi-plus"
      rounded
      variant="outlined"
      severity="secondary"
      aria-label="Add Step"
      class="mt-4"
      @click="addStep"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, defineProps } from "vue"
import { difficultySeverity } from '@/shared/DataTableFunctions'

// Props
const props = defineProps({
    // item could be an array or something else. We'll ensure we handle all cases.
    item: {
        type: [Array, String, Object],
        default: () => []
    },
    title: {
        type: String,
        default: 'New Maintenance Guide'
    }
})

// Local state for steps
const steps = ref([])

// Refs for editing
const editingStep = ref(null)
const editedTime = ref('')
const editedDifficulty = ref('')
// Arrays for Tools/Parts in edit mode
const editedTools = ref([])
const editedParts = ref([])

// Refs for body edit
const editedStepTitle = ref("")
const editedStepText = ref("")

// Base path for images
const basePath = "/images/wiki/"

// Difficulty selection
const selectedOption = ref('')
const selectValue = ref([
    { label: 'Easy', value: 'easy' },
    { label: 'Medium', value: 'medium' },
    { label: 'Hard', value: 'hard' }
])

// Add editedTitle ref
const editedTitle = ref('')

// Lifecycle: Load/parse props.item into steps
onMounted(() => {
    if (Array.isArray(props.item)) {
        steps.value = props.item
        // Set the title from the prop if it's provided
        if (props.title && steps.value[0]) {
            steps.value[0].title = props.title
        }
    } else if (!props.item) {
        steps.value = []
    } else {
        // If it's a string or object, try JSON.parse
        try {
            steps.value = JSON.parse(props.item)
            if (!Array.isArray(steps.value)) {
                steps.value = []
            }
            // Set the title from the prop if it's provided
            if (props.title && steps.value[0]) {
                steps.value[0].title = props.title
            }
        } catch (e) {
            steps.value = []
        }
    }
})

// Toggle editing mode for steps beyond index 0
const toggleEdit = (index) => {
    editingStep.value = editingStep.value === index ? null : index
    if (editingStep.value !== null) {
        editedStepTitle.value = steps.value[index].title || ""
        editedStepText.value = steps.value[index].text || ""
    }
}

// Toggle editing mode for the top step (index 0)
const toggleHeaderEdit = () => {
    editingStep.value = 0
    editedTime.value = steps.value[0]?.time || ''
    editedDifficulty.value = steps.value[0]?.difficulty || ''
    editedTitle.value = steps.value[0]?.title || ''

    // Clone Tools/Parts so we can edit them live in 'editedTools'/'editedParts'
    editedTools.value = steps.value[0]?.tools ? [...steps.value[0].tools] : []
    editedParts.value = steps.value[0]?.parts
        ? steps.value[0].parts.map(obj => ({ ...obj }))
        : []
}

// Save changes for the top step
const toggleHeaderSave = () => {
    if (editingStep.value === 0) {
        if (!steps.value[0]) {
            steps.value[0] = {}
        }
        steps.value[0].time = editedTime.value
        steps.value[0].difficulty = editedDifficulty.value
        steps.value[0].title = editedTitle.value

        // Copy edited arrays into steps[0]
        steps.value[0].tools = [...editedTools.value]
        steps.value[0].parts = editedParts.value.map(obj => ({ ...obj }))

        editingStep.value = null
    }
}

// Save changes for subsequent steps
const toggleSave = (index) => {
    if (editingStep.value === index) {
        steps.value[index].title = editedStepTitle.value
        steps.value[index].text = editedStepText.value
        editingStep.value = null
    }
}

// Add a new step
const addStep = () => {
    const id = steps.value.length + 1
    steps.value.push({
        id,
        title: "",
        image: "",
        text: ""
    })
    // Immediately edit the newly added step
    toggleEdit(steps.value.length - 1)
}

// Confirm before deleting
const confirmDelete = (index) => {
    if (confirm(`Are you sure you want to delete Step ${index + 1}?`)) {
        deleteStep(index)
    }
}

// Delete a step
const deleteStep = (index) => {
    steps.value.splice(index, 1)
    // Re-number steps
    steps.value.forEach((step, idx) => {
        step.id = idx + 1
    })
    // Exit edit mode if we're deleting the step currently in edit mode
    if (editingStep.value === index) {
        editingStep.value = null
    }
}

// Resolve image path (full URL from API is used as-is; otherwise prepend basePath)
const resolveImagePath = (imageName) => {
    if (!imageName) return `${basePath}default-image.png`
    if (imageName.startsWith('http://') || imageName.startsWith('https://') || imageName.startsWith('blob:') || imageName.startsWith('data:')) return imageName
    return `${basePath}${imageName}`
}

// File input change
const onFileChange = (event, index) => {
    const file = event.target.files[0]
    if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
            steps.value[index].image = e.target.result // base64
        }
        reader.readAsDataURL(file)
    }
}

/* ADD / REMOVE Tools & Parts */
const addTool = () => {
    editedTools.value.push("")
}
const removeTool = (i) => {
    editedTools.value.splice(i, 1)
}

const addPart = () => {
    editedParts.value.push({
        qty: 1,
        part: "",
        partNumber: ""
    })
}
const removePart = (i) => {
    editedParts.value.splice(i, 1)
}
</script>

<style scoped>
.steps-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-top: 1rem;
    padding-bottom: 1rem;
}

.step-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: none;
}

.step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f9f9f9;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #ddd;
}

.step-title {
    flex: 1;
    font-size: 1rem;
    color: #888;
    margin-left: 1rem;
}

.step-body {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
}

.step-image {
    flex: 0 0 50%;
    height: 100%;
    overflow: hidden;
}

.step-text {
    flex: 1;
    padding: 1rem;
    font-size: 0.9rem;
    color: #555;
    line-height: 1.4;
}

.multi-panel-step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f9f9f9;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #ddd;
    width: 100%;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
}

.header-right {
    margin-left: 1rem;
}

.guide-title {
    flex: 1;
}

.guide-title h2 {
    color: #455464;
    font-weight: 500;
}

.multi-panel-step-body {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    /* spacing around the Divider if desired */
}

.half-width-panel {
    width: 50%;
    /* Or use flex: 1; for each panel if you'd prefer them to expand/shrink */
}

.image-input {
    margin-top: 0.5rem;
}

.inline-li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    /* space between elements */
}

.holding-image {
    @apply w-full h-48 bg-gray-100 flex items-center justify-center;
    border: 1px solid #e5e7eb;
}
</style>
